import { Radio, TextInput, Tooltip } from '@neo4j-ndl/react';
import { ExclamationTriangleIconSolid, InformationCircleIconOutline } from '@neo4j-ndl/react/icons';
import * as StdLib from '@nx/stdlib';
import * as React from 'react';

import { Details } from '../details';

function ProtocolRadioButton<Value extends StdLib.URLs.ConnectionProtocol>({
  isChecked,
  label,
  tipText,
  tipPlacement = 'bottom',
  value,
  isDisabled,
  onChange,
}: {
  isChecked?: boolean;
  isDisabled?: boolean;
  label: string;
  tipPlacement: 'top' | 'bottom';
  tipText?: string;
  value: Value;
  onChange: (value: Value) => void;
}) {
  return (
    <span className="flex items-center">
      <Radio
        label={label}
        onChange={() => {
          onChange(value);
        }}
        isChecked={isChecked}
        htmlAttributes={{
          value,
          name: 'protocol',
        }}
        isDisabled={isDisabled}
      />
      {StdLib.isNotNullish(tipText) && (
        <Tooltip placement={tipPlacement} type="simple" isPortaled={false}>
          <Tooltip.Trigger hasButtonWrapper>
            <InformationCircleIconOutline className="ml-2 inline-block size-4" aria-label={`${label} explanation`} />
          </Tooltip.Trigger>
          <Tooltip.Content className="max-w-sm">{tipText}</Tooltip.Content>
        </Tooltip>
      )}
    </span>
  );
}

function ProtocolLink(props: { value: StdLib.URLs.ConnectionProtocol }) {
  return (
    <a
      className="n-monospace n-text-palette-primary-text text-[length:inherit]"
      href="https://neo4j.com/docs/aura/getting-started/connect-instance/"
      target="_blank"
      rel="noreferrer"
    >
      <code className="text-[length:inherit]">{props.value}</code>
    </a>
  );
}

function ProtocolHelpText(props: { encrypted: boolean; host?: string }) {
  if (!props.encrypted && props.host !== undefined && !StdLib.URLs.isLocalhost(props.host)) {
    return (
      <span className="text-palette-warning-text">
        <ExclamationTriangleIconSolid className="inline-block h-5 w-5" /> Your browser might not support unencrypted
        connections to non-localhost URLs. Consider using a secure protocol instead.
      </span>
    );
  }

  return props.encrypted ? (
    <span>
      Supports <ProtocolLink value={StdLib.URLs.PROTOCOL_HTTPS} />,{' '}
      <ProtocolLink value={StdLib.URLs.PROTOCOL_NEO4J_SECURE} /> and{' '}
      <ProtocolLink value={StdLib.URLs.PROTOCOL_BOLT_SECURE} />
    </span>
  ) : (
    <span>
      Supports <ProtocolLink value={StdLib.URLs.PROTOCOL_HTTP} />, <ProtocolLink value={StdLib.URLs.PROTOCOL_NEO4J} />{' '}
      and <ProtocolLink value={StdLib.URLs.PROTOCOL_BOLT} />
    </span>
  );
}

interface ConnectionUrlInputProps {
  autoFocus?: boolean;
  errorText?: string;
  isReadOnly?: boolean;
  isOptional?: boolean;
  name?: string;
  showConnectionMethods?: boolean;
  value: string;
  onBlur?: (event: React.SyntheticEvent<HTMLInputElement>) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.SyntheticEvent<HTMLInputElement>) => void;
  onValueChange?: (value: string) => void;
}

function ConnectionUrlInputRenderer(props: ConnectionUrlInputProps, ref: React.ForwardedRef<HTMLInputElement>) {
  const currentProtocol = React.useMemo(() => {
    const neo4jUrl = StdLib.URLs.Neo4jURL.asNullable(props.value, {
      injectProtocol: false,
      replaceHttpProtocol: false,
    });

    if (neo4jUrl === null) {
      return StdLib.URLs.PROTOCOL_NEO4J_SECURE;
    }

    return neo4jUrl.protocol;
  }, [props.value]);

  const neo4jUrl = React.useMemo(() => {
    return StdLib.URLs.Neo4jURL.asNullable(props.value, {
      injectProtocol: true,
      replaceHttpProtocol:
        currentProtocol !== StdLib.URLs.PROTOCOL_HTTPS && currentProtocol !== StdLib.URLs.PROTOCOL_HTTP,
    });
  }, [currentProtocol, props.value]);

  const encryptedConnection = React.useMemo(() => {
    if (neo4jUrl === null) {
      return true;
    }

    return StdLib.URLs.SECURE_PROTOCOLS.some((protocol) => protocol === neo4jUrl.protocol);
  }, [neo4jUrl]);

  function handleProtocolChange(protocol: StdLib.URLs.ConnectionProtocol) {
    if (props.onValueChange === undefined) {
      return;
    }

    if (neo4jUrl !== null) {
      neo4jUrl.protocol = protocol;
      props.onValueChange(neo4jUrl.toString());
    }
  }

  return (
    <div className="flex flex-shrink flex-grow flex-col gap-2">
      {props.showConnectionMethods !== true ? null : (
        <Details label="Connection method">
          <div className="flex flex-col gap-2">
            <ProtocolRadioButton
              isChecked={
                currentProtocol !== StdLib.URLs.PROTOCOL_HTTPS && currentProtocol !== StdLib.URLs.PROTOCOL_HTTP
              }
              isDisabled={neo4jUrl === null}
              label="Neo4j Protocol"
              tipPlacement="top"
              value={encryptedConnection ? StdLib.URLs.PROTOCOL_NEO4J_SECURE : StdLib.URLs.PROTOCOL_NEO4J}
              onChange={handleProtocolChange}
            />
            <ProtocolRadioButton
              isChecked={
                currentProtocol === StdLib.URLs.PROTOCOL_HTTPS || currentProtocol === StdLib.URLs.PROTOCOL_HTTP
              }
              isDisabled={neo4jUrl === null}
              label="HTTP API"
              tipPlacement="top"
              value={encryptedConnection ? StdLib.URLs.PROTOCOL_HTTPS : StdLib.URLs.PROTOCOL_HTTP}
              onChange={handleProtocolChange}
            />
          </div>
        </Details>
      )}
      <TextInput
        ref={ref}
        isFluid
        size="medium"
        label="Connection URL"
        placeholder="neo4j://localhost:7687"
        errorText={props.errorText}
        isReadOnly={props.isReadOnly}
        isOptional={props.isOptional}
        helpText={
          props.isReadOnly === true ? undefined : (
            <ProtocolHelpText encrypted={encryptedConnection} host={neo4jUrl?.host} />
          )
        }
        htmlAttributes={{
          autoFocus: props.autoFocus,
          name: props.name,
          onBlur: props.onBlur,
          onFocus: props.onFocus,
        }}
        onChange={props.onChange}
      />
    </div>
  );
}

export const ConnectionUrlInput = React.forwardRef<HTMLInputElement, ConnectionUrlInputProps>(
  ConnectionUrlInputRenderer,
);
